/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { FC, useMemo } from 'react'
import { Form } from 'react-final-form'
import { MenuSelectField } from 'components/Select/MenuSelectField'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'

import { createFlashCard, createFlashCardVariables } from '__generatedTypes__/createFlashCard'
import { findAllFlashCard_findAllFlashCard_items as IFlashCard } from '__generatedTypes__/findAllFlashCard'
import { findAllFlashCardGroup_findAllFlashCardGroup_items as IGroup } from '__generatedTypes__/findAllFlashCardGroup'
import { removeFlashCard, removeFlashCardVariables } from '__generatedTypes__/removeFlashCard'
import { updateFlashCard, updateFlashCardVariables } from '__generatedTypes__/updateFlashCard'

import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { InputField } from 'components/Input'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import client from 'configs/apolloClient'
import { CREATE_FLASH_CARDS, DELETE_FLASH_CARDS, FIND_ALL_FLASH_CARDS, UPDATE_FLASH_CARDS } from 'queries/flashCards'
import { validateRequired } from 'utils/validation'
import { FieldValues } from 'utils/types'
import SelectMembershipType from 'components/SelectMembershipType'
import { toast } from 'utils/toast'
import * as classes from 'components/shared/GroupCards/commonStyles'

interface IProps {
  flashCard: IFlashCard | { id: string; flashCardGroup: { id: string }; membershipLevel?: { id: string } }
  onClose: () => void
  flashCardGroups: IGroup[]
  flashCardsLength: number
  isEditMode: boolean
}

const ManageFlashCardsModal: FC<IProps> = ({ flashCard, onClose, flashCardsLength, flashCardGroups, isEditMode }) => {
  const [updateFlashCard, { loading: updateLoading }] = useMutation<updateFlashCard, updateFlashCardVariables>(
    UPDATE_FLASH_CARDS
  )
  const [removeFlashCard] = useMutation<removeFlashCard, removeFlashCardVariables>(DELETE_FLASH_CARDS)

  const handleCloseAndUpdate = () => {
    client
      .refetchQueries({
        include: [FIND_ALL_FLASH_CARDS],
      })
      .then(onClose)
  }

  const [createFlashCard, { loading: createLoading }] = useMutation<createFlashCard, createFlashCardVariables>(
    CREATE_FLASH_CARDS
  )

  const handleGroupRemove = async () => {
    if (!flashCard?.id) return

    try {
      await removeFlashCard({ variables: { removeFlashCardId: flashCard.id } })
      handleCloseAndUpdate()

      toast.success('Card removed successfully')
    } catch (error) {
      toast.error('Failed to remove card')
    }
  }

  // TODO: specify proper type
  const handleSubmit = async (data: FieldValues) => {
    if (isEditMode) {
      try {
        await updateFlashCard({
          variables: {
            updateFlashCardInputFieldName: {
              id: data.id,
              name: data.name,
              description: data.description,
              link: data.link,
              membershipLevel: data.membershipLevel.id,
              flashCardGroup: {
                id: data.flashCardGroup.id,
              },
            },
          },
          refetchQueries: () => [
            {
              query: FIND_ALL_FLASH_CARDS,
              variables: {
                query: {
                  take: TAKE_ALL_ITEMS_COUNT,
                  where: {
                    flashCardGroup: {
                      id: data.flashCardGroup.id,
                    },
                  },
                },
              },
            },
          ],
        })
        handleCloseAndUpdate()

        toast.success('Card updated successfully')
      } catch (error) {
        toast.error('Failed to update card')
      }
      //TODO Add error handling
    } else {
      try {
        await createFlashCard({
          variables: {
            createFlashCardInput: {
              name: data.name,
              description: data.description,
              order: flashCardsLength,
              link: data.link,
              membershipLevel: data.membershipLevel.id,
              flashCardGroup: {
                id: data.flashCardGroup.id,
              },
            },
          },
        })
        handleCloseAndUpdate()

        toast.success('Card created successfully')
      } catch (error) {
        toast.error('Failed to create card')
      }
      //TODO Add error handling
    }
  }

  const initialValues = useMemo(
    () =>
      isEditMode
        ? {
            ...flashCard,
            membershipLevel: {
              id: flashCard.membershipLevel,
            },
          }
        : flashCard,
    [flashCard]
  )

  return (
    <div css={classes.modalContainer}>
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values }) => (
          <form css={classes.modalForm} onSubmit={handleSubmit}>
            <Typography css={classes.modalTitle} variant="title2">
              {isEditMode ? 'Edit Card' : 'Create Card'}
            </Typography>
            <InputField name="name" validate={validateRequired} label="Card Title" fullWidth />
            <TextareaAutosize
              value={values.description}
              label="Description"
              validate={validateRequired}
              css={classes.descriptionInput}
              name="description"
            />
            <Typography variant="body2" css={classes.label}>
              Group
            </Typography>
            <MenuSelectField css={classes.select} fullWidth name="flashCardGroup.id" required>
              {flashCardGroups.map((group) => (
                <MenuItem value={group.id} key={group.id}>
                  <div>{group.title}</div>
                </MenuItem>
              ))}
            </MenuSelectField>
            <TextareaAutosize
              label="URL"
              validate={validateRequired}
              css={classes.descriptionInput}
              name="link"
              value={values.link}
            />
            <SelectMembershipType />
            <div css={classes.buttons}>
              {isEditMode && (
                <Button css={classes.button} variant="outlined" color="red" size="large" onClick={handleGroupRemove}>
                  <TrashIcon />
                  Delete
                </Button>
              )}
              <Button css={classes.button} variant="outlined" color="blue" size="large" onClick={onClose}>
                Cancel
              </Button>
              <Button
                css={[classes.button, isEditMode && classes.fullButton]}
                variant="contained"
                type="submit"
                color="blue"
                size="large"
              >
                {/*TODO Change loading after Nazrin`s answer*/}
                {createLoading || updateLoading ? '...Loading' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      />
    </div>
  )
}

export default ManageFlashCardsModal
